import * as THREE from '@phdsoft/three';

export type PivotLockMode = 'vertical' | 'horizontal';

/**
 * World-space lock for pivot-based point insertion ("Lock Vertical" / "Lock Horizontal").
 *
 * Ship models are Z-up, so the lock is gravity-based and fully camera-independent:
 * - 'vertical'  → the inserted point lies on the VERTICAL WORLD LINE through the pivot
 *   (same X/Y station), at whatever height the clicked element provides;
 * - 'horizontal' → the inserted point lies at the PIVOT'S HEIGHT (same Z), at the
 *   clicked element's station.
 *
 * The corrected point is computed on the CLICKED element (surface plane or stiffener
 * line) and only converted back to screen for the downstream insertion raycast.
 *
 * Near-horizontal DECKS are the exception: with the pivot on the deck both gravity
 * constraints degenerate (the vertical line meets the deck AT the pivot, every deck point
 * already has the pivot's height). There the lock is a PLAN line through the pivot along
 * one of the two in-plane world axes X/Y — the one that runs most vertically on screen
 * (`screenVerticalPlanAxis`) — applied by `lockedPlanPoint`. Z is never a candidate, so the
 * toward-camera ranking failure of the earlier screen-based versions cannot recur.
 *
 * History: two earlier versions picked a canonical lock axis by its on-screen direction;
 * both failed UAT because an axis pointing toward the camera can still produce a large
 * radial screen displacement when the pivot is off-centre, winning the ranking and
 * locking a coordinate that is constant on the clicked surface (a visible no-op).
 */
export class PivotAxisLock {
    // built lazily — a `new THREE.Vector3` static initializer runs at module load and
    // breaks under bundles where this module initializes before the three package
    private static get UP(): THREE.Vector3 {
        return new THREE.Vector3(0, 0, 1);
    }
    // a surface steeper than ~30 deg toward the vertical line is intersected by it;
    // flatter surfaces are corrected in-plane instead
    private static readonly MIN_CROSSING = 0.5;
    // minimum |in-plane vertical| for a surface to reach the pivot's height,
    // and minimum |dir.z|/len for a line to do the same
    private static readonly MIN_REACH = 0.2;
    // a pivot closer than this (m, along the vertical) to the clicked plane lies ON it:
    // intersecting the vertical line with the plane would just return the pivot
    private static readonly ON_PLANE_TOLERANCE = 0.1;

    /** Viewport-relative screen position of a world point (null when unprojectable). */
    public static toScreen(camera: THREE.Camera, p: THREE.Vector3): { x: number; y: number } | null {
        const ndc = p.clone().project(camera);
        if (!Number.isFinite(ndc.x) || !Number.isFinite(ndc.y)) return null;
        return {
            x: ((ndc.x + 1) * window.innerWidth) / 2,
            y: (-(ndc.y - 1) * window.innerHeight) / 2,
        };
    }

    /**
     * The in-plane world axis (X or Y) whose screen projection at `at` runs most
     * vertically — the "vertical" lock line direction on a horizontal deck. Scored by
     * the direction of the screen displacement (not its magnitude), X on ties.
     */
    public static screenVerticalPlanAxis(camera: THREE.Camera, at: THREE.Vector3): THREE.Vector3 {
        const origin = PivotAxisLock.toScreen(camera, at);
        let best = new THREE.Vector3(1, 0, 0);
        let bestScore = -1;
        for (const axis of [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0)]) {
            const moved = origin ? PivotAxisLock.toScreen(camera, at.clone().add(axis)) : null;
            if (!moved) continue;
            const dx = Math.abs(moved.x - origin.x);
            const dy = Math.abs(moved.y - origin.y);
            const score = dx + dy > 1e-9 ? dy / (dx + dy) : 0;
            if (score > bestScore) {
                bestScore = score;
                best = axis;
            }
        }
        return best;
    }

    /**
     * Deck lock: the point on the clicked surface that lies, in plan, on the line through
     * the pivot along `planVerticalAxis` ('vertical') or along the perpendicular in-plane
     * axis ('horizontal'). The click slides within its own surface, keeping its height on
     * webs and staying on the deck plane on plates. Null when the surface runs parallel to
     * the lock line (e.g. a web along it) and so cannot reach it; without a normal the
     * plan coordinate is fixed directly.
     */
    public static lockedPlanPoint(
        pivotWorld: THREE.Vector3,
        clickedPoint: THREE.Vector3,
        clickedNormal: { x: number; y: number; z: number } | null | undefined,
        mode: PivotLockMode,
        planVerticalAxis: THREE.Vector3,
    ): THREE.Vector3 | null {
        const up = PivotAxisLock.UP;
        const planVertical = planVerticalAxis.clone().normalize();
        const planHorizontal = new THREE.Vector3().crossVectors(up, planVertical).normalize();
        // the coordinate the lock fixes: across the line, i.e. along the OTHER plan axis
        const across = mode === 'vertical' ? planHorizontal : planVertical;
        const delta = pivotWorld.clone().sub(clickedPoint).dot(across);

        const m = PivotAxisLock.unitNormal(clickedNormal);
        if (!m) {
            return clickedPoint.clone().addScaledVector(across, delta);
        }
        const inPlane = across.clone().addScaledVector(m, -across.dot(m));
        const reach = inPlane.length();
        if (reach < PivotAxisLock.MIN_REACH) return null; // surface parallel to the lock line
        const d = inPlane.divideScalar(reach);
        return clickedPoint.clone().addScaledVector(d, delta / d.dot(across));
    }

    /**
     * The locked world point on the clicked element's surface: 'vertical' returns the
     * point of the surface on (or horizontally closest to) the pivot's vertical line,
     * 'horizontal' the surface point at the pivot's height above/below the click.
     * Null when the surface cannot satisfy the constraint (e.g. horizontal lock on a
     * horizontal deck); without a normal the pivot's coordinates are fixed directly.
     */
    public static lockedWorldPoint(
        pivotWorld: THREE.Vector3,
        clickedPoint: THREE.Vector3,
        clickedNormal: { x: number; y: number; z: number } | null | undefined,
        mode: PivotLockMode,
    ): THREE.Vector3 | null {
        const m = PivotAxisLock.unitNormal(clickedNormal);
        const up = PivotAxisLock.UP;

        if (mode === 'vertical') {
            if (!m) {
                return new THREE.Vector3(pivotWorld.x, pivotWorld.y, clickedPoint.z);
            }
            const crossing = m.dot(up);
            if (Math.abs(crossing) > PivotAxisLock.MIN_CROSSING) {
                // the surface crosses the pivot's vertical line (webs, decks, tilted
                // plating): intersect the line with the surface plane — unless the pivot
                // lies ON that plane, where the intersection is the pivot itself and the
                // in-plane station lock below is the meaningful answer
                const t = m.dot(clickedPoint.clone().sub(pivotWorld)) / crossing;
                if (Math.abs(t) >= PivotAxisLock.ON_PLANE_TOLERANCE) {
                    return pivotWorld.clone().addScaledVector(up, t);
                }
            }
            // near-vertical surface (shell/bulkhead plating) or pivot on the clicked
            // plane: keep the click's height and slide the point along the in-plane
            // horizontal to the pivot's station
            const h = new THREE.Vector3().crossVectors(up, m);
            if (h.lengthSq() < 1e-12) return null;
            h.normalize();
            const t = pivotWorld.clone().sub(clickedPoint).dot(h);
            return clickedPoint.clone().addScaledVector(h, t);
        }

        // 'horizontal': bring the click to the pivot's height within the clicked surface
        if (!m) {
            return new THREE.Vector3(clickedPoint.x, clickedPoint.y, pivotWorld.z);
        }
        const inPlaneVertical = up.clone().addScaledVector(m, -up.dot(m));
        const reach = inPlaneVertical.length();
        if (reach < PivotAxisLock.MIN_REACH) return null; // horizontal surface: height is fixed
        const v = inPlaneVertical.divideScalar(reach);
        return clickedPoint.clone().addScaledVector(v, (pivotWorld.z - clickedPoint.z) / v.dot(up));
    }

    /**
     * Station on the (infinite) line through start/end satisfying the lock: 'vertical'
     * → the horizontally-closest point to the pivot's vertical line, 'horizontal' → the
     * point at the pivot's height. Returns the line parameter u (0 at start, 1 at end —
     * may fall outside for stations beyond this segment, letting the caller pick the
     * sibling element that contains it) and the world point. Null when the line cannot
     * satisfy the constraint (vertical member for 'vertical', horizontal for 'horizontal').
     */
    public static stationOnLineForMode(
        start: THREE.Vector3,
        end: THREE.Vector3,
        pivotWorld: THREE.Vector3,
        mode: PivotLockMode,
    ): { u: number; point: THREE.Vector3 } | null {
        const dir = end.clone().sub(start);
        const len = dir.length();
        if (len < 1e-9) return null;

        let u: number;
        if (mode === 'horizontal') {
            if (Math.abs(dir.z / len) < PivotAxisLock.MIN_REACH) return null;
            u = (pivotWorld.z - start.z) / dir.z;
        } else {
            const dH = new THREE.Vector3(dir.x, dir.y, 0);
            const lenH = dH.length();
            if (lenH / len < PivotAxisLock.MIN_REACH) return null;
            // least-squares closest approach to the pivot's vertical line, in the horizontal plane
            const toPivotH = new THREE.Vector3(pivotWorld.x - start.x, pivotWorld.y - start.y, 0);
            u = toPivotH.dot(dH) / (lenH * lenH);
        }
        return { u, point: start.clone().addScaledVector(dir, u) };
    }

    private static unitNormal(n: { x: number; y: number; z: number } | null | undefined): THREE.Vector3 | null {
        if (!n) return null;
        const m = new THREE.Vector3(n.x, n.y, n.z);
        if (m.lengthSq() < 1e-12) return null;
        return m.normalize();
    }
}
